/**
 * The global model switcher. Admin picks a provider, then the main (chat)
 * and subagent models from that provider's registered catalog; Save opens
 * a confirmation dialog before committing UpdateChatModelSettings. The
 * model is invisible to end users — this card is the only place it's set.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { useModelSettings, useUpdateModelSettings } from './hooks';
/**
 * Pre-fill per provider: OpenAI runs cost-sensitive Luna; single-tier providers
 * run one model for both.
 *
 * The preferred id tracks the compiled default in shanks' catalog
 * (services/shanks/pkg/config/models.go) and the stored global setting pangaea
 * migrations move. When it is missing from the served list — a catalog it
 * predates, or a provider key not loaded in this environment — the `??` chain
 * falls through to the provider's first entry rather than pre-filling a model
 * the switcher cannot offer.
 */ const OPENAI_PREFERRED_MODEL_ID = 'openai-gpt-6-luna';
function defaultsForProvider(provider, models) {
    const list = models.filter((m)=>m.provider === provider);
    const preferred = provider === 'openai' ? list.find((m)=>m.id === OPENAI_PREFERRED_MODEL_ID)?.id : undefined;
    const main = preferred ?? list[0]?.id ?? '';
    const subagent = preferred ?? main;
    return {
        main,
        subagent
    };
}
export function ModelSettingsCard() {
    const { data: settings, isLoading } = useModelSettings();
    const update = useUpdateModelSettings();
    const { user } = useAuth();
    const [draft, setDraft] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // The model setting is a single GLOBAL row, so switching is Specra staff
    // (internal) only; tenant admins see the switcher read-only. Mirrors the
    // backend gate (requireAdmin CanSwitch); this is UX, not enforcement.
    const readOnly = (user?.role ?? '').toLowerCase() !== 'internal';
    const models = settings?.availableModels ?? [];
    const providers = [
        ...new Set(models.map((m)=>m.provider))
    ];
    const nameOf = (id)=>models.find((m)=>m.id === id)?.name ?? id;
    const activeMain = settings?.effectiveMainModelId ?? '';
    const activeSubagent = settings?.effectiveSubagentModelId ?? '';
    const activeProvider = models.find((m)=>m.id === activeMain)?.provider ?? '';
    const main = draft?.main ?? activeMain;
    const subagent = draft?.subagent ?? activeSubagent;
    const provider = models.find((m)=>m.id === main)?.provider ?? activeProvider;
    const providerModels = models.filter((m)=>m.provider === provider);
    const dirty = draft !== null && (main !== activeMain || subagent !== activeSubagent);
    return /*#__PURE__*/ _jsxs(Card, {
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-start justify-between gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(CardTitle, {
                                    children: "Active model"
                                }),
                                /*#__PURE__*/ _jsx(CardDescription, {
                                    children: "Applies to every user's chats. End users never see or pick a model."
                                })
                            ]
                        }),
                        isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-6 w-64"
                        }) : /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 text-sm",
                            "data-testid": Testids.adminUsage.activeSummary,
                            "data-provider": activeProvider,
                            "data-main-model": activeMain,
                            "data-subagent-model": activeSubagent,
                            children: [
                                /*#__PURE__*/ _jsx(Badge, {
                                    variant: "outline",
                                    className: "capitalize",
                                    children: activeProvider
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: nameOf(activeMain)
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-muted-foreground",
                                    children: [
                                        "· subagents: ",
                                        nameOf(activeSubagent)
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                className: "flex flex-wrap items-end gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Provider"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: provider,
                                disabled: readOnly,
                                onValueChange: (p)=>setDraft(defaultsForProvider(p, models)),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-44 capitalize",
                                        "data-testid": Testids.adminUsage.providerSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Provider"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: providers.map((p)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: p,
                                                className: "capitalize",
                                                children: p
                                            }, p))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Chat model"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: main,
                                disabled: readOnly,
                                onValueChange: (id)=>setDraft({
                                        main: id,
                                        subagent
                                    }),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-64",
                                        "data-testid": Testids.adminUsage.mainModelSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Model"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: providerModels.map((m)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: m.id,
                                                "data-model-id": m.id,
                                                children: m.name
                                            }, m.id))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                children: "Subagent model"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: subagent,
                                disabled: readOnly,
                                onValueChange: (id)=>setDraft({
                                        main,
                                        subagent: id
                                    }),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-64",
                                        "data-testid": Testids.adminUsage.subagentModelSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Model"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: providerModels.map((m)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: m.id,
                                                "data-model-id": m.id,
                                                children: m.name
                                            }, m.id))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        "data-testid": Testids.adminUsage.saveButton,
                        disabled: readOnly || !dirty || update.isPending,
                        onClick: ()=>setConfirmOpen(true),
                        children: "Save"
                    }),
                    readOnly && /*#__PURE__*/ _jsx("p", {
                        className: "basis-full text-xs text-muted-foreground",
                        children: "Read-only: the model is shared by every customer, so only Specra staff can switch it."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Dialog, {
                open: confirmOpen,
                onOpenChange: setConfirmOpen,
                children: /*#__PURE__*/ _jsxs(DialogContent, {
                    "data-testid": Testids.adminUsage.confirmDialog,
                    children: [
                        /*#__PURE__*/ _jsxs(DialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(DialogTitle, {
                                    children: "Switch the active model?"
                                }),
                                /*#__PURE__*/ _jsxs(DialogDescription, {
                                    children: [
                                        "Every user's chats will run on ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: nameOf(main)
                                        }),
                                        ' ',
                                        "(subagents: ",
                                        nameOf(subagent),
                                        ") starting with their next message. Takes effect within seconds, no redeploy."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    onClick: ()=>setConfirmOpen(false),
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    "data-testid": Testids.adminUsage.confirmButton,
                                    disabled: update.isPending,
                                    onClick: ()=>update.mutate({
                                            mainModelId: main,
                                            subagentModelId: subagent
                                        }, {
                                            onSuccess: ()=>{
                                                setDraft(null);
                                                setConfirmOpen(false);
                                            }
                                        }),
                                    children: "Switch model"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
